import { isCreditKind, isInFlight } from '@platform/domain';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { FilePlus2, Landmark, RefreshCw, Send, Undo2, XCircle } from 'lucide-react';
import { Fragment, useId, useMemo, useState, type ReactNode } from 'react';
import { isApiError } from '../../../api/client';
import { salesApi } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queries';
import type {
  InvoiceKind,
  SalesDocumentDetail,
  StateChangeRequestView,
  StateChangeView,
  WindowView,
} from '../../../api/types';
import { TestEnvStrip } from '../../../app/shell/TestEnvStrip';
import { useAccess } from '../../../app/access';
import type { Dictionary } from '../../../i18n/en';
import { useI18n } from '../../../i18n/I18nProvider';
import { UserText } from '../../../ui/Bidi';
import { Button } from '../../../ui/Button';
import { RadioCards } from '../../../ui/Choice';
import { ConfirmDialog, ModalDialog } from '../../../ui/Dialog';
import { Money } from '../../../ui/Layout';
import { TextField } from '../../../ui/TextField';
import { useToast } from '../../../ui/Toast';
import styles from '../Sales.module.css';
import { MockLabel } from '../status';

/**
 * «إجراءات لدى المصلحة» (BLOCK-11-PLAN D-3, D-5; document-cancellation §6; notes-list-detail.md):
 * a full-width band in the page flow under the journey, never a side or sticky column and never a
 * tab (AC-T6). It shows what the server allows and nothing more: the window line (computed at
 * render, never ticking: AC-T5), ETA's parameters and reported dates verbatim and isolated
 * (AC-T9), the state of each request, the watch, and the allowed actions as labelled buttons. The
 * page toolbar never gains a Tax Authority action (AC-T1). Every confirmation is a centred dialog
 * with the test-environment strip and the facts as a `dl` (AC-T3).
 */

// H-5 (D-13): "in flight" is the ONE predicate in @platform/domain (`isInFlight`); the local
// `['queued','claimed','sending','unknown']` set is gone, so this file and the domain never diverge.
const WATCHED: ReadonlySet<string> = new Set(['submitted', 'valid', 'rejected', 'cancelled']);
/**
 * Refusal codes where a repeat of the same request would fail the same way (RB3R11-06): the action
 * is hidden after such a refusal, so the band never offers a doomed retry, and the refusal line
 * carries a real next step instead (`a.nextStep`).
 */
const NO_RETRY: ReadonlySet<string> = new Set([
  'eta_forbidden',
  'eta_auth_failed',
  'active_referencing_documents',
  'document_frozen',
]);

type View = StateChangeView;
type DateKey = keyof NonNullable<View['pending']>;
const DATE_KEYS: readonly DateKey[] = [
  'cancelRequestDate',
  'cancelRequestDelayedDate',
  'rejectRequestDate',
  'rejectRequestDelayedDate',
  'declineCancelRequestDate',
  'declineRejectRequestDate',
];

/** The latest request of each action (the history keeps every one). */
function latestByAction(requests: readonly StateChangeRequestView[]) {
  const out = new Map<string, StateChangeRequestView>();
  for (const r of requests) out.set(r.action, r);
  return out;
}

/** Whether the band asks the page to keep reading (a request or a reading is under way). */
export function etaActionsBusy(eta: Partial<View> | null | undefined): boolean {
  if (!eta?.requests) return false;
  const pendingRead =
    eta.watch?.checkRequestedAt &&
    (!eta.watch.detailsSyncedAt ||
      Date.parse(eta.watch.checkRequestedAt) > Date.parse(eta.watch.detailsSyncedAt));
  return (
    Boolean(pendingRead) ||
    eta.requests.some(
      (r) =>
        isInFlight(r) ||
        (r.status === 'done' && r.confirmedAt === null) ||
        (r.status === 'done_unconfirmed' && r.releaseRequestedAt !== null),
    )
  );
}

/** "3 hours" since the anchor, computed once when the band is drawn (AC-T5: it never ticks). */
export function elapsedText(t: Dictionary, ms: number): string {
  const e = t.detail.actions.elapsed;
  // Whole units, by integer division (no floating-point rounding helpers in this code base).
  const whole = (value: number, unit: number) => (value - (value % unit)) / unit;
  const minutes = Math.max(1, whole(Math.max(0, ms), 60_000));
  if (minutes < 60) return e.minutes(minutes);
  const hours = whole(minutes, 60);
  if (hours < 48) return e.hours(hours);
  return e.days(whole(hours, 24));
}

/** ETA's own text (a parameter name, a code, a reported date): Latin, isolated left to right.
 *  `wrap` lets a long quoted name break at word boundaries instead of anywhere, so its guillemets
 *  never strand on a phone line (W3-04 / RB3R11-13). */
function EtaCode({
  children,
  testId,
  wrap,
}: {
  children: ReactNode;
  testId?: string;
  wrap?: boolean;
}) {
  return (
    <code
      dir="ltr"
      lang="en"
      className={wrap ? `${styles.etaCode} ${styles.etaCodeWrap}` : styles.etaCode}
      data-testid={testId}
    >
      {children}
    </code>
  );
}

function asText(value: unknown): string {
  if (value === null || value === undefined) return '—';
  return typeof value === 'string' ? value : JSON.stringify(value);
}

export function EtaActions({
  orgId,
  detail,
  now = Date.now(),
}: {
  orgId: string;
  detail: SalesDocumentDetail;
  /** The render time (tests pin it); the band never re-renders on a timer. */
  now?: number;
}) {
  const { t } = useI18n();
  const a = t.detail.actions;
  const access = useAccess(orgId);
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const titleId = useId();
  const [dialog, setDialog] = useState<'cancel' | 'decline' | 'release' | null>(null);
  const eta = detail.eta;
  const doc = detail.document;
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: [...queryKeys.org(orgId), 'sales'] });

  // A refusal from the server: our sentence for its reason, then the latest state (D-2, N-03).
  const onRefused = async (error: unknown) => {
    if (isApiError(error) && error.code === 'version_conflict')
      toast.error(error, a.versionChanged);
    else if (isApiError(error) && error.code === 'document_state_conflict') {
      const reason = typeof error.details?.reason === 'string' ? error.details.reason : '';
      toast.error(error, a.refusal[reason] ?? undefined);
    } else toast.error(error);
    await refresh();
  };

  const check = useMutation({
    mutationFn: () => salesApi.checkAtEta(orgId, doc.id),
    onSuccess: () => refresh(),
    onError: (error) => onRefused(error),
  });

  if (!eta || !eta.allowed || !eta.windows || !WATCHED.has(eta.state.status)) return null;
  const view = eta as unknown as View;
  const allowed = eta.allowed;
  const windows = eta.windows;
  const status = eta.state.status;
  const requests = latestByAction(eta.requests ?? []);
  const cancelReq = requests.get('cancel');
  const declineReq = requests.get('decline_rejection');
  const pending = eta.pending;
  const isMock = eta.parameters?.mock === true;
  const frozen = eta.freeze?.frozen === true;
  const inFlight = (eta.requests ?? []).some(isInFlight);
  const checkPending = Boolean(
    eta.watch?.checkRequestedAt &&
    (!eta.watch.detailsSyncedAt ||
      Date.parse(eta.watch.checkRequestedAt) > Date.parse(eta.watch.detailsSyncedAt)),
  );
  const releasePending = Boolean(
    cancelReq && cancelReq.status === 'done_unconfirmed' && cancelReq.releaseRequestedAt,
  );
  // The last release decision, from the history (R3-01: a refusal clears the pending release).
  const lastRelease = [...detail.events]
    .reverse()
    .find((e) =>
      [
        'eta_cancel_release_requested',
        'eta_cancel_release_refused',
        'eta_cancel_released',
      ].includes(e.event),
    );

  // The window line of the action that fits the state: cancel (submitted, valid), decline (rejected).
  const windowAction: 'cancel' | 'decline_rejection' | null =
    status === 'rejected'
      ? 'decline_rejection'
      : status === 'submitted' || status === 'valid'
        ? 'cancel'
        : null;
  const verdict = windowAction ? allowed[windowAction] : null;
  const showWindow =
    windowAction !== null &&
    verdict !== null &&
    (verdict.allowed || verdict.reason === 'eta_window_closed');

  // A retry that would fail the same way is not offered (RB3R11-06): a refusal with a NO_RETRY code
  // hides the action; the refusal line's next step says what to change instead.
  const cancelBlocked =
    cancelReq?.status === 'refused' && cancelReq.code !== null && NO_RETRY.has(cancelReq.code);
  const declineBlocked =
    declineReq?.status === 'refused' && declineReq.code !== null && NO_RETRY.has(declineReq.code);
  const canCancel = allowed.cancel.allowed && access.can('invoice.cancel') && !cancelBlocked;
  const canDecline =
    allowed.decline_rejection.allowed && access.can('invoice.decline') && !declineBlocked;
  const canRelease =
    cancelReq?.status === 'done_unconfirmed' &&
    cancelReq.confirmedAt === null &&
    !cancelReq.releaseRequestedAt &&
    access.can('invoice.cancel');

  // The start actions on a valid invoice's page (sales-notes.md §6): create a credit or debit note
  // with the reference filled in. A note's own page never offers them. They live in the Tax
  // Authority actions band and are a correction at the Authority, so they are gated by the
  // cancel-at-the-Tax-Authority key (B11-PRM-01: a sales user, who lacks it, sees no action in
  // this band; D-22: issuing a credit note needs that key; note.needsCancelPermission). `invoice.
  // create` is still required to draft. Hide, never disable (D-5). The export invoice starts the
  // export variant of each note.
  const isInvoice = doc.kind === 'i' || doc.kind === 'ei';
  const canStartNote =
    isInvoice && status === 'valid' && access.can('invoice.create') && access.can('invoice.cancel');
  const creditKind: InvoiceKind = doc.kind === 'ei' ? 'ec' : 'c';
  const debitKind: InvoiceKind = doc.kind === 'ei' ? 'ed' : 'd';
  const startNote = (kind: InvoiceKind) =>
    void navigate({
      to: '/orgs/$orgId/sales/invoices/new',
      params: { orgId },
      search: { kind, from: doc.id },
    });

  // D-13 (F-17; AC-T6): a cancellation blocked by live notes names those notes, as links. The
  // reason is the ONE predicate's `active_referencing_documents`; `eta.liveNotes` carries them.
  const blockingNotes =
    allowed.cancel.reason === 'active_referencing_documents' ? (eta.liveNotes ?? []) : [];

  return (
    <section
      className={`${styles.card} ${styles.etaActions}`}
      aria-labelledby={titleId}
      data-testid="eta-actions"
      data-status={status}
    >
      <div className={styles.cardHead}>
        <h2 id={titleId} className={styles.cardTitle}>
          <span className={styles.cardIcon} aria-hidden="true">
            <Landmark size={16} />
          </span>
          {a.title}
        </h2>
        <MockLabel environment={eta.state.environment} />
      </div>
      <div className={`${styles.cardBody} ${styles.etaBand}`}>
        <StateLines view={view} status={status} frozen={frozen} />

        {[cancelReq, declineReq].map((r) =>
          r ? (
            <RequestLine
              key={r.id}
              request={r}
              releaseEvent={r.action === 'cancel' ? lastRelease : undefined}
            />
          ) : null,
        )}

        {blockingNotes.length > 0 ? (
          <p className={styles.etaLine} data-testid="eta-blocking-notes">
            {`${a.outcome.active_referencing_documents}: `}
            {blockingNotes.map((n, i) => (
              <Fragment key={n.id}>
                {i > 0 ? ' · ' : ''}
                <Link
                  to="/orgs/$orgId/sales/invoices/$docId"
                  params={{ orgId, docId: n.id }}
                  className={styles.mono}
                  data-testid="eta-blocking-note"
                >
                  {n.number ?? '—'}
                </Link>
              </Fragment>
            ))}
          </p>
        ) : null}

        {showWindow && windowAction ? (
          <WindowLine
            window={windows[windowAction]}
            label={windowAction === 'cancel' ? a.cancelPeriod : a.declinePeriod}
            mock={isMock}
            now={now}
            issuedAt={doc.dateTimeIssued}
          />
        ) : null}
        {verdict?.reason === 'eta_window_closed' ? (
          <p className={styles.etaLine} data-testid="eta-window-closed-note">
            {windowAction === 'cancel' ? a.cancelClosed : a.declineClosed}
          </p>
        ) : null}

        <Parameters view={view} action={windowAction} />
        <ReportedDates pending={pending ?? null} />

        {eta.watch?.ended ? (
          <p className={styles.etaLine} data-testid="eta-watch-ended">
            {a.watchEnded(eta.watch.until ? cairoDateTime(eta.watch.until) : '—')}
          </p>
        ) : null}
        {checkPending || releasePending ? (
          <p className={styles.etaLine} data-testid="eta-check-requested" role="status">
            {a.checkRequested}
          </p>
        ) : null}

        <div className={styles.etaButtons}>
          {canCancel ? (
            <Button
              variant="secondary"
              icon={<XCircle size={16} aria-hidden="true" />}
              onPress={() => setDialog('cancel')}
              testId="eta-cancel"
            >
              {a.cancelAction}
            </Button>
          ) : null}
          {canDecline ? (
            <>
              {allowed.decline_rejection.frozenWarning ? (
                <p className={styles.etaLine} data-testid="eta-frozen-warning">
                  {a.frozenWarning}
                </p>
              ) : null}
              <Button
                variant="secondary"
                icon={<Send size={16} aria-hidden="true" />}
                onPress={() => setDialog('decline')}
                testId="eta-decline"
              >
                {a.declineAction}
              </Button>
            </>
          ) : null}
          {canRelease ? (
            <Button
              variant="secondary"
              icon={<Undo2 size={16} aria-hidden="true" />}
              onPress={() => setDialog('release')}
              testId="eta-release"
              className={styles.etaWrapButton}
            >
              {a.release}
            </Button>
          ) : null}
          {eta.watch?.ended && !checkPending && !inFlight && access.can('invoice.view') ? (
            <Button
              variant="secondary"
              icon={<RefreshCw size={16} aria-hidden="true" />}
              isPending={check.isPending}
              onPress={() => check.mutate()}
              testId="eta-check"
            >
              {a.check}
            </Button>
          ) : null}
          {canStartNote ? (
            <>
              <Button
                variant="secondary"
                icon={<FilePlus2 size={16} aria-hidden="true" />}
                onPress={() => startNote(creditKind)}
                testId="start-credit-note"
              >
                {t.detail.createCreditFromInvoice}
              </Button>
              <Button
                variant="secondary"
                icon={<FilePlus2 size={16} aria-hidden="true" />}
                onPress={() => startNote(debitKind)}
                testId="start-debit-note"
              >
                {t.detail.createDebitFromInvoice}
              </Button>
            </>
          ) : null}
        </div>
      </div>

      <CancelDialog
        isOpen={dialog === 'cancel'}
        onClose={() => setDialog(null)}
        orgId={orgId}
        detail={detail}
        windowLine={<WindowText window={windows.cancel} now={now} issuedAt={doc.dateTimeIssued} />}
        onRefused={onRefused}
        onDone={refresh}
      />
      <DeclineDialog
        isOpen={dialog === 'decline'}
        onClose={() => setDialog(null)}
        orgId={orgId}
        detail={detail}
        windowLine={
          <WindowText window={windows.decline_rejection} now={now} issuedAt={doc.dateTimeIssued} />
        }
        onRefused={onRefused}
        onDone={refresh}
      />
      {cancelReq ? (
        <ReleaseDialog
          isOpen={dialog === 'release'}
          onClose={() => setDialog(null)}
          orgId={orgId}
          detail={detail}
          requestId={cancelReq.id}
          onRefused={onRefused}
          onDone={refresh}
        />
      ) : null}
    </section>
  );
}

/** The document's state at ETA, as the band words it (the badge carries the status itself). */
function StateLines({ view, status, frozen }: { view: View; status: string; frozen: boolean }) {
  const { t } = useI18n();
  const a = t.detail.actions;
  const p = view.pending;
  const lines: ReactNode[] = [];
  if (frozen)
    lines.push(
      <p key="frozen" className={styles.etaLine} data-testid="eta-frozen">
        {a.frozen}
      </p>,
    );
  // G-3 (F-5): `cancelled` has the SAME two readings here as on the timeline, and from the same
  // predicate the server computed (`view.cancellationSettled` =
  // `windowOf('decline_cancellation', …) === 'closed'`). While that period is open, uncertain or
  // unknown the band says the buyer may still object, so the line can never contradict the hedged
  // timeline entry on the same screen.
  if (status === 'cancelled')
    lines.push(
      <p
        key="cancelled"
        className={styles.etaLine}
        data-testid="eta-state-line"
        data-settled={view.cancellationSettled ? 'yes' : 'no'}
      >
        {view.cancellationSettled ? a.cancelledSettled : a.cancelledPendingObjection}
      </p>,
    );
  else if (status === 'rejected')
    lines.push(
      <p key="rejected" className={styles.etaLine} data-testid="eta-state-line">
        {a.rejected}
      </p>,
    );
  else if (p?.declineCancelRequestDate)
    lines.push(
      <p key="declined" className={styles.etaLine} data-testid="eta-cancel-declined">
        {fill(a.cancelDeclined('\u0000'), <EtaCode>{p.declineCancelRequestDate}</EtaCode>)}
      </p>,
    );
  else if (p?.declineRejectRequestDate)
    lines.push(
      <p key="rejection-declined" className={styles.etaLine} data-testid="eta-state-line">
        {a.rejectionDeclined}
      </p>,
    );
  else if (view.cancelPending && p?.cancelRequestDate)
    lines.push(
      <p key="cancel-requested" className={styles.etaLine} data-testid="eta-state-line">
        {a.cancelRequested}
      </p>,
    );
  return <>{lines}</>;
}

/** Splits a translated sentence at a NUL placeholder and puts a node in its place. */
function fill(sentence: string, node: ReactNode): ReactNode {
  const [before, after] = sentence.split('\u0000');
  return (
    <>
      {before}
      {node}
      {after}
    </>
  );
}

function RequestLine({
  request,
  releaseEvent,
}: {
  request: StateChangeRequestView;
  releaseEvent: SalesDocumentDetail['events'][number] | undefined;
}) {
  const { t } = useI18n();
  const a = t.detail.actions;
  const label = a.requestOf[request.action];
  const common = {
    'data-testid': 'eta-request-line',
    'data-action': request.action,
    'data-status': request.status,
  };
  if (isInFlight(request) || (request.status === 'done' && !request.confirmedAt))
    return (
      <p className={styles.etaLine} {...common}>
        {`${label}: ${a.checking}`}
      </p>
    );
  if (request.status === 'done_unconfirmed')
    return (
      <>
        <p className={styles.etaLine} {...common}>
          {`${label}: ${a.unconfirmed}`}
        </p>
        {releaseEvent?.event === 'eta_cancel_release_refused' &&
        !request.releaseRequestedAt &&
        releaseEvent.reason?.startsWith('release_not_clean') ? (
          <p className={styles.etaLine} data-testid="eta-release-refused">
            {a.releaseRefused}{' '}
            <EtaCode>{releaseEvent.reason.replace(/^release_not_clean: /, '')}</EtaCode>
          </p>
        ) : null}
      </>
    );
  if (request.status === 'released')
    return (
      <p className={styles.etaLine} {...common}>
        {a.released}
      </p>
    );
  if (request.status === 'refused') {
    const code = request.code ?? '';
    const text = a.outcome[code] ?? a.refusal[code] ?? a.outcome.eta_refused;
    const step = a.nextStep[code];
    return (
      <>
        <p className={styles.etaLine} {...common}>
          {`${label}: ${request.refusalSource === 'local' ? a.refusedBy.local : a.refusedBy.eta} — ${text} `}
          {request.etaErrorCode ? (
            <EtaCode testId="eta-request-code">{request.etaErrorCode}</EtaCode>
          ) : null}
        </p>
        {step ? (
          <p className={`${styles.etaLine} ${styles.subtle}`} data-testid="eta-next-step">
            {step}
          </p>
        ) : null}
      </>
    );
  }
  if (request.status === 'not_applied')
    return (
      <p className={styles.etaLine} {...common}>
        {`${label}: ${a.notApplied}`}
      </p>
    );
  if (request.status === 'failed')
    return (
      <p className={styles.etaLine} {...common}>
        {`${label}: ${a.failed}`}
      </p>
    );
  return null;
}

/** The window as one sentence; the elapsed time of an unknown window is computed at render. */
function WindowText({
  window: w,
  now,
  issuedAt,
}: {
  window: WindowView;
  now: number;
  issuedAt: string | null;
}) {
  const { t, formatDateTime } = useI18n();
  const a = t.detail.actions;
  if (w.state === 'open' && w.earliestDeadline)
    return <>{a.open(formatDateTime(w.earliestDeadline))}</>;
  if (w.state === 'uncertain') return <>{a.uncertain}</>;
  if (w.state === 'closed')
    return <>{w.latestDeadline ? a.closed(formatDateTime(w.latestDeadline)) : a.closedNoDate}</>;
  const anchor = w.anchors.latest ?? w.anchors.earliest;
  const at = anchor?.at ?? (issuedAt ? Date.parse(issuedAt) : now);
  const event =
    w.action === 'decline_rejection' || w.action === 'decline_cancellation'
      ? a.sinceRejection
      : anchor?.source === 'dateTimeIssued'
        ? a.sinceIssue
        : a.sinceSubmission;
  return <>{a.unknown(elapsedText(t, now - at), event)}</>;
}

function WindowLine({
  window: w,
  label,
  mock,
  now,
  issuedAt,
}: {
  window: WindowView;
  label: string;
  mock: boolean;
  now: number;
  issuedAt: string | null;
}) {
  const { t } = useI18n();
  return (
    <p
      className={styles.etaLine}
      data-testid="eta-window-line"
      data-state={w.state}
      data-deadline={(w.state === 'open' ? w.earliestDeadline : w.latestDeadline) ?? ''}
    >
      <span className={styles.etaLineLabel}>{label}: </span>
      <WindowText window={w} now={now} issuedAt={issuedAt} />
      {mock && w.parameter ? (
        <span className={styles.etaSimTag} data-testid="eta-simulator-value">
          {' '}
          {t.detail.actions.simulatorValue}
        </span>
      ) : null}
    </p>
  );
}

/**
 * «تُبلغ المصلحة: «…» = …»: ETA's parameters as it sent them, verbatim (T11-02), but each shown
 * ONLY under the action it governs (RB3R11-02 / W3-03): the governing action comes from the server's
 * `meta` (environment-agnostic; ETA-W7) and falls back to the raw row's `action` in the mock. Where
 * a parameter has several values at once, each value's validity period is shown so two values are
 * not read as contradictory (RB3R11-05). The quoted name wraps at word boundaries on a phone so its
 * guillemets never strand (W3-04).
 */
function Parameters({
  view,
  action,
}: {
  view: View;
  action: 'cancel' | 'decline_rejection' | null;
}) {
  const { t } = useI18n();
  const a = t.detail.actions;
  const params = view.parameters;
  if (!params) return null;
  if (params.items.length === 0)
    return (
      <p className={`${styles.etaLine} ${styles.subtle}`} data-testid="eta-no-parameters">
        {a.noParameters}
      </p>
    );
  const enriched = params.items.map((p, i) => ({
    p,
    i,
    governs: params.meta?.[i]?.governs ?? (typeof p.action === 'string' ? p.action : null),
  }));
  const shown = action ? enriched.filter((x) => x.governs === action) : enriched;
  if (shown.length === 0)
    return (
      <p className={`${styles.etaLine} ${styles.subtle}`} data-testid="eta-no-parameters">
        {a.noParametersForAction}
      </p>
    );
  // Several values for one parameter name (different validity periods): show each value's dates.
  const nameCount = new Map<string, number>();
  for (const x of shown)
    nameCount.set(asText(x.p.parameter), (nameCount.get(asText(x.p.parameter)) ?? 0) + 1);
  return (
    <ul className={styles.etaParams}>
      {shown.map(({ p, i }) => {
        const several = (nameCount.get(asText(p.parameter)) ?? 0) > 1;
        const hasDates = p.activeFrom != null || p.activeTo != null;
        return (
          <li key={`${asText(p.id)}-${i}`} data-testid="eta-parameter">
            {a.reports}{' '}
            <EtaCode testId="eta-parameter-name" wrap>
              «{asText(p.parameter)}»
            </EtaCode>{' '}
            = <EtaCode testId="eta-parameter-value">{asText(p.value)}</EtaCode>
            {several && hasDates ? (
              <span className={styles.subtle} data-testid="eta-parameter-validity">
                {' ('}
                {a.validFrom} <EtaCode>{asText(p.activeFrom)}</EtaCode>{' '}
                {p.activeTo != null ? (
                  <>
                    {a.validTo} <EtaCode>{asText(p.activeTo)}</EtaCode>
                  </>
                ) : (
                  a.validOpen
                )}
                {')'}
              </span>
            ) : null}
            {params.mock ? <span className={styles.etaSimTag}> {a.simulatorValue}</span> : null}
          </li>
        );
      })}
    </ul>
  );
}

/** «تواريخ أبلغت بها المصلحة»: the request, delayed and decline dates, verbatim (D-3). */
/** ETA reports instants in UTC; the reader's deadline is Cairo time (WP-11.3b, RB3R11-01).
 *  A value that is not a date is returned untouched: ETA's own text is never rewritten. */
function cairoDateTime(v: unknown): string {
  const s = asText(v);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const p = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Africa/Cairo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
  return `${p} (القاهرة)`;
}

function ReportedDates({ pending }: { pending: View['pending'] }) {
  const { t } = useI18n();
  const a = t.detail.actions;
  const shown = DATE_KEYS.filter((k) => pending?.[k]);
  if (!pending || shown.length === 0) return null;
  return (
    <div className={styles.etaDates} data-testid="eta-dates">
      <p className={styles.sectionLabel}>{a.dates}</p>
      <dl className={styles.kv}>
        {shown.map((k) => (
          <div key={k} style={{ display: 'contents' }}>
            <dt>{a.dateLabels[k]}</dt>
            <dd>
              <EtaCode testId={`eta-date-${k}`}>{cairoDateTime(pending[k])}</EtaCode>
              <span className={styles.subtle}>
                {' '}
                {a.reports} <EtaCode testId={`eta-date-raw-${k}`}>{asText(pending[k])}</EtaCode>
              </span>
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** The facts every confirmation shows (AC-T3): number, buyer, amount due, period. */
function Facts({ detail, period }: { detail: SalesDocumentDetail; period?: ReactNode }) {
  const { t } = useI18n();
  const c = t.detail.actions.cancelDialog;
  // Block 11 (role-4 B-1): a credit note's due box reads «المستحق للمشتري», like the PDF and page.
  const isCredit = isCreditKind(detail.document.kind);
  return (
    <dl className={styles.kv} data-testid="eta-dialog-facts">
      <dt>{c.number}</dt>
      <dd className={styles.mono}>{detail.document.number ?? '—'}</dd>
      <dt>{c.buyer}</dt>
      <dd>
        {detail.receiver?.cash ? (
          t.sales.cash
        ) : (
          <UserText>{detail.receiver?.name ?? t.sales.noCustomer}</UserText>
        )}
      </dd>
      <dt>{isCredit ? t.detail.amountDueCreditEgp : t.detail.amountDueEgp}</dt>
      <dd>
        <Money value={detail.totals?.amountDue} currency="EGP" />
      </dd>
      {period ? (
        <>
          <dt>{c.period}</dt>
          <dd>{period}</dd>
        </>
      ) : null}
    </dl>
  );
}

type ReasonChoice = 'buyer' | 'document' | 'other';

function CancelDialog({
  isOpen,
  onClose,
  orgId,
  detail,
  windowLine,
  onRefused,
  onDone,
}: {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  detail: SalesDocumentDetail;
  windowLine: ReactNode;
  onRefused: (error: unknown) => Promise<void>;
  onDone: () => Promise<void>;
}) {
  const { t } = useI18n();
  const c = t.detail.actions.cancelDialog;
  const toast = useToast();
  const [choice, setChoice] = useState<ReasonChoice | ''>('');
  const [reason, setReason] = useState('');
  const known = useMemo(
    () => new Set((detail.eta?.requests ?? []).map((r) => r.id)),
    [detail.eta?.requests],
  );
  const send = useMutation({
    mutationFn: () =>
      salesApi.cancelAtEta(orgId, detail.document.id, reason, detail.eta?.etaStateSeq ?? ''),
    onSuccess: async (r) => {
      onClose();
      toast.success(known.has(r.id) ? c.same : c.queued);
      await onDone();
    },
    onError: async (error) => {
      onClose();
      await onRefused(error);
    },
  });
  const trimmed = reason.trim();
  const tooLong = [...reason].length > 500;
  const close = () => {
    if (send.isPending) return;
    setChoice('');
    setReason('');
    onClose();
  };
  return (
    <ModalDialog
      isOpen={isOpen}
      onOpenChange={(open) => !open && close()}
      title={c.title}
      isDismissable={!send.isPending}
    >
      <div className={styles.etaDialogBody} data-testid="eta-cancel-dialog">
        <TestEnvStrip compact />
        <p>{c.lead}</p>
        <Facts detail={detail} period={windowLine} />
        <RadioCards<ReasonChoice>
          label={c.reasonGroup}
          value={choice as ReasonChoice}
          onChange={(v) => {
            setChoice(v);
            setReason(v === 'buyer' ? c.reasonBuyer : v === 'document' ? c.reasonDocument : '');
          }}
          options={[
            { value: 'buyer', title: c.reasonBuyer, testId: 'eta-reason-buyer' },
            { value: 'document', title: c.reasonDocument, testId: 'eta-reason-document' },
            { value: 'other', title: c.reasonOther, testId: 'eta-reason-other' },
          ]}
          testId="eta-reason-choice"
        />
        <TextField
          label={c.reasonText}
          description={c.reasonHelp}
          value={reason}
          onChange={setReason}
          multiline
          rows={2}
          fieldName="reason"
          {...(tooLong ? { errorMessage: t.issues.reason_too_long ?? c.reasonHelp } : {})}
        />
      </div>
      <div className={styles.etaDialogActions}>
        <Button variant="secondary" onPress={close} isDisabled={send.isPending}>
          {t.common.notNow}
        </Button>
        <Button
          variant="danger"
          onPress={() => send.mutate()}
          isPending={send.isPending}
          isDisabled={trimmed === '' || tooLong}
          testId="confirm-action"
        >
          {c.confirm}
        </Button>
      </div>
    </ModalDialog>
  );
}

function DeclineDialog({
  isOpen,
  onClose,
  orgId,
  detail,
  windowLine,
  onRefused,
  onDone,
}: {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  detail: SalesDocumentDetail;
  windowLine: ReactNode;
  onRefused: (error: unknown) => Promise<void>;
  onDone: () => Promise<void>;
}) {
  const { t } = useI18n();
  const d = t.detail.actions.declineDialog;
  const toast = useToast();
  const send = useMutation({
    mutationFn: () =>
      salesApi.declineRejection(orgId, detail.document.id, detail.eta?.etaStateSeq ?? ''),
    onSuccess: async () => {
      onClose();
      toast.success(d.queued);
      await onDone();
    },
    onError: async (error) => {
      onClose();
      await onRefused(error);
    },
  });
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title={d.title}
      confirmLabel={d.confirm}
      isPending={send.isPending}
      onConfirm={() => send.mutate()}
    >
      <div className={styles.etaDialogBody} data-testid="eta-decline-dialog">
        <TestEnvStrip compact />
        <p>{d.lead}</p>
        <Facts detail={detail} period={windowLine} />
      </div>
    </ConfirmDialog>
  );
}

function ReleaseDialog({
  isOpen,
  onClose,
  orgId,
  detail,
  requestId,
  onRefused,
  onDone,
}: {
  isOpen: boolean;
  onClose: () => void;
  orgId: string;
  detail: SalesDocumentDetail;
  requestId: string;
  onRefused: (error: unknown) => Promise<void>;
  onDone: () => Promise<void>;
}) {
  const { t } = useI18n();
  const r = t.detail.actions.releaseDialog;
  const toast = useToast();
  const send = useMutation({
    mutationFn: () => salesApi.releaseRequest(orgId, detail.document.id, requestId),
    onSuccess: async () => {
      onClose();
      toast.success(r.queued);
      await onDone();
    },
    onError: async (error) => {
      onClose();
      await onRefused(error);
    },
  });
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title={r.title}
      confirmLabel={r.confirm}
      isPending={send.isPending}
      onConfirm={() => send.mutate()}
    >
      <div className={styles.etaDialogBody} data-testid="eta-release-dialog">
        <TestEnvStrip compact />
        <p>{r.lead}</p>
        <Facts detail={detail} />
      </div>
    </ConfirmDialog>
  );
}
